/**
 * # Direct reads from the domain API
 *
 * The browser's side of the user-token scheme. Compliance pages used to reach
 * `apps/api` only through the workspace server's `/api/amoreg/*` proxy, which
 * put a second hop and a second session check in front of every read. Now
 * the server mints the browser a short-lived token once
 * (`POST /api/amoreg/auth/api-token`, proven by the same-site cookie), and
 * reads go straight to the API with that token as a bearer.
 *
 * ## Where the token lives
 *
 * In a module-level variable, and nowhere else. It is never written to
 * `localStorage`, `sessionStorage`, a cookie, or the URL, so it does not
 * outlive the tab, cannot be read by another origin, and is not sent to any
 * host but the API origin the server named. Signing out drops it
 * ({@link invalidateDomainApiSession} is called from `performSignOut`).
 *
 * ## Lifecycle
 *
 * ```text
 *  read requested
 *    │
 *    ├─ no direct path for this read ─────────────────────────▶ proxy
 *    ├─ on proxy hold (recent failure) ────────────────────────▶ proxy
 *    ├─ cached token with > 1 min left ───────────────────────▶ API
 *    └─ otherwise: mint (one in flight, shared by all callers)
 *         ├─ 200 ──────────────────────────────────────────────▶ API
 *         └─ anything else / network error → hold 1 min ───────▶ proxy
 * ```
 *
 * The proxy is the fallback for every case in which the direct path is not
 * available: a server without the secret, a session that is not a person, a
 * signed-out cookie, an API origin the browser cannot reach, an API that
 * rejects the server's tokens. Falling back is silent and sticky for
 * {@link PROXY_HOLD_MS}, so a broken direct path costs one failed request,
 * not one per read. Nothing here can make a page emptier than the proxy
 * would.
 *
 * ## Safety properties
 *
 * - The token is only ever sent to `apiBaseUrl` as returned by the server.
 *   A read's `apiPath` is appended to that origin; it is never a full URL a
 *   caller could point elsewhere.
 * - Direct requests carry no cookies (`credentials: "omit"`), so the
 *   workspace session is never exposed to the API origin, even one that
 *   shares the workspace origin.
 * - A single mint is shared by concurrent callers; the page's nine parallel
 *   reads cost one mint, not nine.
 * - Refresh happens {@link REFRESH_MARGIN_MS} before expiry, so a request is
 *   never sent with a token about to die mid-flight.
 *
 * @module state/domainApi
 * @see complianceFetch.ts — the only consumer; it also handles a refused
 *   token (re-mint once) and an unreachable origin (fall back).
 * @see packages/platform-shared/src/userToken.ts — the token format and
 *   threat model
 */
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

/** A read that can be served by the API directly, with its proxy twin. */
export interface ComplianceTarget {
  /** The `/api/amoreg/*` URL on the workspace server; always valid. */
  readonly proxyUrl: string;
  /** The API path (`/api/v1/organizations/…`), or `null` when the read has no direct equivalent. */
  readonly apiPath: string | null;
}

/** A minted token and where to use it. */
export interface DomainApiSession {
  /** API origin, no trailing slash. */
  readonly baseUrl: string;
  /** Bearer value. Never log it. */
  readonly token: string;
  /** Epoch milliseconds. */
  readonly expiresAt: number;
}

/** Where and how one read should be sent, decided at request time. */
export interface ResolvedComplianceRequest {
  readonly url: string;
  readonly init: RequestInit;
  /** `true` when `url` is on the API origin (and `init` carries the bearer). */
  readonly direct: boolean;
  /** The bearer sent, so a refusal can name exactly which token was rejected. */
  readonly token: string | null;
}

/** The server's mint route. */
export const MINT_PATH = "/api/amoreg/auth/api-token";

/** Re-mint this long before expiry so no request carries a dying token. */
export const REFRESH_MARGIN_MS = 60_000;

/** After a failed mint or an unreachable API, stay on the proxy this long. */
export const PROXY_HOLD_MS = 60_000;

/**
 * Longest a mint may take. Every direct read waits on the shared mint, so a
 * stalled request must fail, start the proxy hold, and release the reads —
 * never hang them.
 */
export const MINT_TIMEOUT_MS = 10_000;

let session: DomainApiSession | null = null;
let pending: Promise<DomainApiSession | null> | null = null;
/** Epoch milliseconds until which reads go through the proxy without minting. */
let proxyUntil = 0;
/**
 * Bumped by every invalidation. A mint that started under an older
 * generation must not store its result: the session it was minted for (a
 * user who has since signed out) is gone, and its token must not become the
 * next user's credential.
 */
let generation = 0;

const MintResponse = Schema.Struct({
  token: Schema.String,
  expiresAt: Schema.String,
  apiBaseUrl: Schema.String,
});

/** Schema failures stay private; a rejected mint always falls back to the proxy. */
const readMintResponse = async (response: Response): Promise<DomainApiSession | null> => {
  if (!response.ok) {
    await response.body?.cancel();
    return null;
  }
  const { token, expiresAt, apiBaseUrl } = Schema.decodeUnknownSync(MintResponse)(await response.json());
  const expiresAtMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresAtMs)) return null;
  return { baseUrl: apiBaseUrl.replace(/\/$/, ""), token, expiresAt: expiresAtMs };
};

/** A shared mint owns one interruptible deadline, including its response body.
 * Credential POSTs are never automatically retried. */
const mint = (): Promise<DomainApiSession | null> => Effect.runPromise(
  Effect.tryPromise({
    try: async (signal) => readMintResponse(await fetch(resolvePrimaryEnvironmentHttpUrl(MINT_PATH), {
      method: "POST",
      credentials: "include",
      signal,
    })),
    // Do not keep a token, response body, or request in the failure channel.
    catch: () => null,
  }).pipe(
    Effect.timeoutOrElse({ duration: MINT_TIMEOUT_MS, orElse: () => Effect.succeed(null) }),
    Effect.orElseSucceed(() => null),
  ),
);

/**
 * The current direct-API session, minting or refreshing when needed.
 *
 * @param now - Injectable clock for tests; epoch milliseconds.
 * @returns The session to use, or `null` meaning "use the proxy".
 *   Concurrent callers share one in-flight mint.
 */
export function resolveDomainApiSession(now: number = Date.now()): Promise<DomainApiSession | null> {
  if (session !== null && session.expiresAt - REFRESH_MARGIN_MS > now) {
    return Promise.resolve(session);
  }
  if (now < proxyUntil) return Promise.resolve(null);
  if (pending !== null) return pending;
  const startedAt = generation;
  const attempt = mint()
    .then((minted) => {
      // Invalidated while in flight (sign-out, refusal): discard, never store.
      if (generation !== startedAt) return null;
      if (minted === null) proxyUntil = Date.now() + PROXY_HOLD_MS;
      session = minted;
      return minted;
    })
    .finally(() => {
      if (pending === attempt) pending = null;
    });
  pending = attempt;
  return attempt;
}

/**
 * Drop the cached token. The next read mints again.
 *
 * Called when the API rejects a token (it may have expired or the secret
 * rotated) and on sign-out, so a token never outlives the session that
 * obtained it in this tab.
 *
 * @param refusedToken - When given, only that token is dropped. Several
 *   reads may be in flight with the same token; the first refusal re-mints,
 *   and the refusals that arrive afterwards must not throw the fresh token
 *   away or interrupt the mint that is producing it. Omit it for sign-out,
 *   which drops whatever is there.
 */
export function invalidateDomainApiSession(refusedToken?: string): void {
  if (refusedToken !== undefined) {
    if (session === null || session.token !== refusedToken) return;
    session = null;
    // A refresh already in flight (the token was near expiry) is exactly the
    // replacement this refusal wants; let it land rather than discard it.
    if (pending === null) generation += 1;
    return;
  }
  session = null;
  generation += 1;
}

/**
 * The API origin could not be reached, or refuses this server's tokens.
 * Drop the token and route reads through the proxy for {@link PROXY_HOLD_MS}.
 */
export function markDomainApiUnreachable(now: number = Date.now()): void {
  session = null;
  generation += 1;
  proxyUntil = now + PROXY_HOLD_MS;
}

/**
 * Where and how to send a compliance read right now.
 *
 * A plain string target, or one without an `apiPath`, always resolves to the
 * proxy with the session cookie. A target with an `apiPath` resolves to the
 * API with the bearer when a session is available, and to the proxy
 * otherwise. The decision is made per request, so the fallback state is
 * always current.
 */
export async function resolveComplianceRequest(
  target: string | ComplianceTarget,
): Promise<ResolvedComplianceRequest> {
  const proxyUrl = typeof target === "string" ? target : target.proxyUrl;
  const apiPath = typeof target === "string" ? null : target.apiPath;
  const proxy: ResolvedComplianceRequest = {
    url: proxyUrl,
    init: { credentials: "include" },
    direct: false,
    token: null,
  };
  if (apiPath === null) return proxy;
  const current = await resolveDomainApiSession();
  if (current === null) return proxy;
  return {
    url: `${current.baseUrl}${apiPath}`,
    // Cookies are omitted explicitly: the bearer is the only credential, and
    // the workspace session cookie must not travel to the API even when the
    // API happens to share the workspace origin.
    init: { credentials: "omit", headers: { authorization: `Bearer ${current.token}` } },
    direct: true,
    token: current.token,
  };
}

/** Test hook: forget every cached decision. */
export function __resetDomainApiForTests(): void {
  session = null;
  pending = null;
  proxyUntil = 0;
  generation += 1;
}
